import { Controller } from "@hotwired/stimulus";

import { prefersReducedMotion } from "~/lib/viewport";
import { clamp01 } from "~/lib/web/ease";
import { whileInView } from "~/lib/web/loop";
import { registerScrub } from "~/lib/web/scrub";

/** Fraction of a viewport the copy takes to fade out. */
const COPY_FADE_DISTANCE = 0.5;

export default class WebHeroController extends Controller<HTMLElement> {
  static override targets = ["fading", "video"];
  declare readonly fadingTargets: HTMLElement[];
  declare readonly videoTarget: HTMLVideoElement;
  declare readonly hasVideoTarget: boolean;

  private unregister: (() => void) | null = null;
  private stopWatching: (() => void) | null = null;
  private listeners = new AbortController();

  override connect(): void {
    if (prefersReducedMotion()) return;

    if (this.fadingTargets.length) {
      // Computed from the rect rather than from a one-shot trigger, so it is
      // correct scrolling up as well as down.
      this.unregister = registerScrub(this.element, (rect, viewportHeight) => {
        const opacity = (
          1 - clamp01(-rect.top / (viewportHeight * COPY_FADE_DISTANCE))
        ).toFixed(3);
        for (const element of this.fadingTargets)
          element.style.opacity = opacity;
      });
    }

    if (!this.hasVideoTarget) return;
    if (document.readyState === "complete") this.attachClip();
    else {
      window.addEventListener("load", () => this.attachClip(), {
        once: true,
        signal: this.listeners.signal,
      });
    }
  }

  override disconnect(): void {
    this.unregister?.();
    this.unregister = null;
    this.stopWatching?.();
    this.stopWatching = null;
    this.listeners.abort();
    this.listeners = new AbortController();
  }

  /**
   * The clip is attached after load rather than declared in the markup. An
   * `autoplay` attribute makes the browser fetch the video regardless of
   * `preload="none"`, which puts three quarters of a megabyte ahead of the
   * poster the LCP is measured on.
   */
  private attachClip(): void {
    const video = this.videoTarget;
    const sources: [string | undefined, string][] = [
      [video.dataset["webm"], "video/webm"],
      [video.dataset["mp4"], "video/mp4"],
    ];
    for (const [src, type] of sources) {
      if (src === undefined || !src) continue;
      const source = document.createElement("source");
      source.src = src;
      source.type = type;
      video.appendChild(source);
    }
    video.addEventListener(
      "playing",
      () => this.element.classList.add("is-playing"),
      { signal: this.listeners.signal },
    );
    // preload="none" is the markup's hint for "do not fetch before the page has
    // loaded". Leaving it set past that tells the browser to keep waiting and
    // the clip never buffers.
    video.preload = "auto";
    video.load();
    // Autoplay can still be refused, on a metered connection or under a data
    // saver. The poster is already painted and remains, so a refusal needs no
    // handling beyond catching the rejection.
    //
    // Out of view the clip is still decoded, which costs power for as long as
    // the reader is on the rest of the page.
    this.stopWatching = whileInView(
      this.element,
      () => void video.play().catch(() => {}),
      () => video.pause(),
    );
  }
}
